/**
 * Player Bundle for Export
 *
 * This module provides the compiled player bundle for course exports.
 *
 * IMPORTANT: The player bundle is built from apps/player using:
 *   npm run build:player-export
 * (which builds the export player AND copies it into player-bundle/; the
 * authoring vite/vitest configs run this automatically when the bundle is
 * missing or stale — see scripts/ensurePlayerBundle.js.)
 *
 * After building, the bundle files are imported here using Vite's ?raw import.
 * This embeds the player code directly into the authoring app bundle.
 */

// Import the built player bundle files as raw strings
// These files are generated by running build:export in the player workspace
import playerJs from '../../player-bundle/player-v6.js?raw';
import playerCss from '../../player-bundle/player-v6.css?raw';
import sourceSansUrl from '../../player-bundle/assets/SourceSans3-VariableFont_wght.ttf?url';
import sourceSansItalicUrl from '../../player-bundle/assets/SourceSans3-Italic-VariableFont_wght.ttf?url';
import celebrationUrl from '../../player-bundle/assets/celebration.png?url';
import highfiveUrl from '../../player-bundle/assets/highfive.png?url';
import ribbonUrl from '../../player-bundle/assets/ribbon.png?url';
import trophyUrl from '../../player-bundle/assets/trophy.png?url';
import wreathUrl from '../../player-bundle/assets/wreath.png?url';
import emaUrl from '../../player-bundle/assets/ema.png?url';
import emtrainLogoDefaultUrl from '../../player-bundle/assets/emtrain-default.png?url';

export interface PlayerBundleAsset {
  /** Browser-resolved asset URL inside authoring */
  sourceUrl: string;
  /** Relative output path inside the exported package */
  outputPath: string;
}

export interface PlayerBundle {
  /** Compiled JavaScript bundle */
  js: string;
  /** Compiled CSS styles */
  css: string;
  /** Additional runtime assets that must ship alongside JS/CSS */
  assets: PlayerBundleAsset[];
}

function rewritePlayerJsAssetUrls(js: string): string {
  return js.replaceAll('/assets/', 'assets/');
}

function rewritePlayerCssAssetUrls(css: string): string {
  return css.replaceAll('/assets/', '../assets/');
}

/**
 * Get the compiled player bundle for export packages
 * @returns The player bundle with JS and CSS as strings
 */
export function getPlayerBundle(): PlayerBundle {
  return {
    js: rewritePlayerJsAssetUrls(playerJs),
    css: rewritePlayerCssAssetUrls(playerCss),
    assets: [
      {
        sourceUrl: sourceSansUrl,
        outputPath: 'assets/SourceSans3-VariableFont_wght.ttf',
      },
      {
        sourceUrl: sourceSansItalicUrl,
        outputPath: 'assets/SourceSans3-Italic-VariableFont_wght.ttf',
      },
      {
        sourceUrl: celebrationUrl,
        outputPath: 'assets/celebration.png',
      },
      {
        sourceUrl: highfiveUrl,
        outputPath: 'assets/highfive.png',
      },
      {
        sourceUrl: ribbonUrl,
        outputPath: 'assets/ribbon.png',
      },
      {
        sourceUrl: trophyUrl,
        outputPath: 'assets/trophy.png',
      },
      {
        sourceUrl: wreathUrl,
        outputPath: 'assets/wreath.png',
      },
      {
        sourceUrl: emaUrl,
        outputPath: 'assets/ema.png',
      },
      {
        sourceUrl: emtrainLogoDefaultUrl,
        outputPath: 'assets/emtrain-default.png',
      },
    ],
  };
}
